<template>
  <div>
    <b-card
      no-body
      :class="isDark ? 'analytics-hearings analytics-hearings--dark' : 'analytics-hearings analytics-hearings--light'"
    >
      <template #header>
        <b-col>
          <b-row class="justify-content-between align-items-center">
            <b-col class="p-0 m-0">
              <p class="p-0 m-0 analytics-hearings__title">
                Listed Cases
              </p>
            </b-col>
            <b-col>
              <b-row class="justify-content-end align-items-center">
                <hearings-calendar-button class="mr-1" />
                <hearing-filter class="pr-50" />
                <hearing-download-button />
              </b-row>
            </b-col>
          </b-row>
        </b-col>
      </template>

      <!-- Tabs for Confirmed and Tentative -->
      <div class="custom-tabs-row mt-1">
        <div
          :class="['custom-tab', activeTab === 0 ? 'active' : '', isDark ? 'custom-tab--dark' : 'custom-tab--light']"
          @click="activeTab = 0"
        >
          <span :class="['custom-tab-title', activeTab === 0 ? 'custom-tab-title--active' : '']">Confirmed</span>
        </div>
        <div
          :class="['custom-tab', activeTab === 1 ? 'active' : '', 'custom-tab--tentative', isDark ? 'custom-tab--dark' : 'custom-tab--light']"
          @click="activeTab = 1"
        >
          <span :class="['custom-tab-title', activeTab === 1 ? 'custom-tab-title--active-tentative' : '']">Tentative</span>
        </div>
      </div>

      <b-overlay
        :show="hearingLoading && !filteredHearings.length"
        rounded="sm"
        variant="transparent"
        opacity="0.5"
        blur="2px"
      >
        <div class="analytics-hearings__body">
          <div v-if="filteredHearings.length">
            <b-card
              v-for="item in filteredHearings"
              :key="item.date"
              no-body
              class="m-0 p-0 rounded-0"
            >
              <b-list-group flush>
                <b-list-group-item class="flex-column align-items-start">
                  <div class="d-flex w-100 justify-content-between">
                    <h4 class="mb-1 hearing-date--confirmed">
                      Hearing Date: {{ item.date }}
                    </h4>
                    <small class="text-secondary">{{ item.data.length }} Cases</small>
                  </div>
                  <analytics-hearing-item-list
                    :items="item.data"
                    :hearing_date="item.date"
                    :is-tentative="activeTab === 1"
                  />
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </div>

          <p
            v-if="!filteredHearings.length && !hearingLoading"
            class="text-center mt-5"
          >
            Record Not Found!
          </p>
        </div>
      </b-overlay>

      <b-card-footer class="m-0 p-0">
        <b-col>
          <b-row class="justify-content-center p-1">
            <b-button
              variant="outline-info"
              :disabled="!filteredHearings.length"
              @click="$router.push({ name: 'cause-list' })"
            >
              View More
            </b-button>
          </b-row>
        </b-col>
      </b-card-footer>
    </b-card>
  </div>
</template>

<script>
import useAppConfig from '@core/app-config/useAppConfig'
import { computed } from '@vue/composition-api'
import moment from 'moment'
import {
  BButton,
  BCard,
  BCardFooter,
  BCol,
  BListGroup,
  BListGroupItem,
  BOverlay,
  BRow,
  VBTooltip,
  BTabs,
  BTab,
} from 'bootstrap-vue'
import HearingDownloadButton from '../causelist/HearingDownloadButton.vue'
import HearingFilter from '../causelist/HearingFilter.vue'
import AnalyticsHearingItemList from './components/AnalyticsHearingItemList.vue'
import HearingsCalendarButton from './components/HearingsCalendarButton.vue'

export default {
  components: {
    BButton,
    BCard,
    BCardFooter,
    BCol,
    BListGroup,
    BListGroupItem,
    BOverlay,
    BRow,
    HearingDownloadButton,
    HearingFilter,
    AnalyticsHearingItemList,
    HearingsCalendarButton,
    BTabs,
    BTab,
  },
  directives: { 'b-tooltip': VBTooltip },
  data() {
    return {
      interval: null,
      activeTab: 0, // 0: Confirmed, 1: Tentative
    }
  },
  computed: {
    hearings() {
      return this.$store.state.hearingStore.allHearings || []
    },
    hearingLoading() {
      return this.$store.state.hearingStore.hearingLoading
    },
    filteredHearings() {
      // Tab 0: Confirmed, Tab 1: Tentative
      if (this.activeTab === 1) {
        // Tentative: filter for both matterStatus and matter_status
        return this.hearings
          .map(item => ({
            ...item,
            data: (item.data || []).filter(h => h.matter_status === 'TENTATIVE'),
          }))
          .filter(item => item.data.length > 0)
      }
      // Confirmed: all except tentative
      return this.hearings
        .map(item => ({
          ...item,
          data: (item.data || []).filter(h => h.matter_status !== 'TENTATIVE'),
        }))
        .filter(item => item.data.length > 0)
    },
  },
  beforeCreate() {
    this.$store.dispatch('hearingStore/fetchHearings')
  },
  beforeMount() {
    this.startInterval()
  },
  beforeDestroy() {
    clearInterval(this.interval)
  },
  methods: {
    startInterval() {
      const beforeTime = 17
      const afterTime = 9
      const now = Number(moment().format('HH'))
      const day = moment().format('dddd')

      if (day !== 'Sunday' && now > afterTime && now < beforeTime) {
        this.$store.dispatch('displayboardStore/fetchUserDisplayBoard')
        clearInterval(this.interval)
        this.interval = setInterval(() => {
          this.$store.dispatch('displayboardStore/fetchUserDisplayBoard')
        }, 30000)
      }
    },
  },
  setup() {
    const { skin } = useAppConfig()
    const isDark = computed(() => skin.value === 'dark')

    return { isDark, skin }
  },
}
</script>

<style scoped>
.analytics-hearings--dark {
  background-color: #343d55;
  border-color: #343d55;
  --hearing-card-bg: #283046;
  --hearing-card-text: #d0d2d6;
  --hearing-title: #7ab8f5;
  --hearing-tab-active: #7ab8f5;
  --hearing-tab-bg: #3b4253;
  --hearing-tab-tentative: #ff9800;
  --hearing-date-confirmed: #d0d2d6;
  --hearing-date-tentative: #ff9800;
}

.analytics-hearings--light {
  background-color: #f3f2f7;
  border-color: #e6e9eb;
  --hearing-card-bg: #ffffff;
  --hearing-card-text: #5e5873;
  --hearing-title: #7367f0;
  --hearing-tab-active: #7367f0;
  --hearing-tab-bg: #ebe9f1;
  --hearing-tab-tentative: #ff9800;
  --hearing-date-confirmed: #5e5873;
  --hearing-date-tentative: #ff9800;
}

.analytics-hearings__title {
  font-weight: 600;
}

.analytics-hearings__body {
  max-height: 530px;
  min-height: 529px;
  overflow-y: scroll;
}

.custom-tabs-row {
  display: flex;
  width: 100%;
  margin-bottom: 1.5rem;
}
.custom-tab {
  flex: 1 1 0;
  text-align: center;
  padding: 18px 0 10px 0;
  font-size: 1rem;
  font-weight: 500;
  color: #d1d5db;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}
.custom-tab.active {
  color: var(--hearing-tab-active);
  background: var(--hearing-tab-bg);
}
.custom-tab.active::after {
  content: '';
  display: block;
  height: 4px;
  background: var(--hearing-tab-active);
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 2px 2px 0 0;
}

.custom-tab--tentative.active::after {
  background: var(--hearing-tab-active);
}
.custom-tab-title {
  font-weight: 500;
  font-size: 1rem;
  transition: color 0.2s;
  color: var(--hearing-card-text);
}
.custom-tab-title--active {
  color: var(--hearing-tab-active) !important;
}
.custom-tab-title--active-tentative {
  color: var(--hearing-tab-active) !important;
}

.hearing-date--confirmed {
  color: var(--hearing-card-text);
}
.hearing-date--tentative {
  color: var(--hearing-date-tentative, #ff9800);
}
</style>
